import { computed, reactive, ref, unref } from 'vue';

const RESOURCE_KEYS = [
  'assistants',
  'actions',
  'runs',
  'memories',
  'approvals',
  'scenarios',
  'teams',
];

export const createAgentResourceErrors = () =>
  Object.fromEntries(RESOURCE_KEYS.map(key => [key, '']));

export function useAgentResourceOrchestration({
  assistantId,
  store,
  canManage,
  toolsEnabled,
  approvalsEnabled,
  managementEnabled,
  syncForm,
  notify,
  t,
}) {
  const isLoading = ref(false);
  const hasLoaded = ref(false);
  const loadError = ref('');
  const resourceErrors = reactive(createAgentResourceErrors());
  const isInitialLoading = computed(() => isLoading.value && !hasLoaded.value);
  const isRefreshing = computed(() => isLoading.value && hasLoaded.value);
  const hasResourceErrors = computed(() =>
    Object.values(resourceErrors).some(Boolean)
  );

  let pageDataRequestId = 0;
  let realtimeRefreshTimer;
  let loaders = {};

  const configureLoaders = nextLoaders => {
    loaders = { ...loaders, ...nextLoaders };
  };

  const readResourceError = error =>
    error?.message || t('CAPTAIN_EXPERIENCE.WORKSPACE.PARTIAL_LOAD_ERROR');

  const clearResourceErrors = () => {
    Object.keys(resourceErrors).forEach(key => {
      resourceErrors[key] = '';
    });
  };

  const loadOptionalResource = async (resource, loader, isCurrent) => {
    try {
      await loader();
      if (isCurrent()) resourceErrors[resource] = '';
    } catch (error) {
      if (isCurrent()) resourceErrors[resource] = readResourceError(error);
    }
  };

  const optionalLoadersFor = (targetAssistantId, isCurrent) => {
    const tasks = [];
    if (unref(toolsEnabled) && unref(canManage)) {
      tasks.push(
        loadOptionalResource(
          'actions',
          () => loaders.actions(targetAssistantId),
          isCurrent
        )
      );
    }
    if (unref(approvalsEnabled) && unref(canManage)) {
      tasks.push(
        loadOptionalResource(
          'runs',
          () => loaders.runs(targetAssistantId),
          isCurrent
        ),
        loadOptionalResource(
          'approvals',
          () => loaders.approvals(targetAssistantId),
          isCurrent
        )
      );
    }
    if (unref(managementEnabled) && unref(canManage)) {
      tasks.push(
        loadOptionalResource(
          'memories',
          () => loaders.memories(targetAssistantId),
          isCurrent
        ),
        loadOptionalResource(
          'scenarios',
          () =>
            store.dispatch('captainScenarios/get', {
              assistantId: targetAssistantId,
            }),
          isCurrent
        )
      );
    }
    return tasks;
  };

  const isStaleRequest = (requestId, targetAssistantId) =>
    requestId !== pageDataRequestId || targetAssistantId !== unref(assistantId);

  const fetchPageData = async (targetAssistantId = unref(assistantId)) => {
    pageDataRequestId += 1;
    const requestId = pageDataRequestId;
    const isCurrent = () => !isStaleRequest(requestId, targetAssistantId);
    isLoading.value = true;
    loadError.value = '';
    clearResourceErrors();

    try {
      const [assistantsResult, teamsResult] = await Promise.allSettled([
        store.dispatch('captainAssistants/get'),
        store.dispatch('teams/get'),
      ]);
      if (assistantsResult.status === 'rejected' && isCurrent()) {
        resourceErrors.assistants = readResourceError(assistantsResult.reason);
      }
      if (teamsResult.status === 'rejected' && isCurrent()) {
        resourceErrors.teams = readResourceError(teamsResult.reason);
      }
      if (!targetAssistantId || !isCurrent()) return;

      await store.dispatch('captainAssistants/show', targetAssistantId);
      if (!isCurrent()) return;
      syncForm();

      await Promise.all(optionalLoadersFor(targetAssistantId, isCurrent));
      if (!isCurrent()) return;
      hasLoaded.value = true;
    } catch (error) {
      if (!isCurrent()) return;
      loadError.value =
        error?.message ||
        t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_LOAD_AGENTS_ERROR');
      notify(loadError.value);
    } finally {
      if (requestId === pageDataRequestId) isLoading.value = false;
    }
  };

  const resetForAssistant = targetAssistantId => {
    pageDataRequestId += 1;
    hasLoaded.value = false;
    loadError.value = '';
    if (targetAssistantId) fetchPageData(targetAssistantId);
  };

  const refreshActivityFromRealtime = data => {
    if (
      Number(data?.assistant_id) !== unref(assistantId) ||
      !unref(approvalsEnabled) ||
      !unref(canManage)
    ) {
      return;
    }

    clearTimeout(realtimeRefreshTimer);
    realtimeRefreshTimer = setTimeout(() => {
      Promise.all([loaders.runs(), loaders.approvals()]).catch(error => {
        notify(
          error?.message ||
            t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_REFRESH_ASSISTANT_ERROR')
        );
      });
    }, 300);
  };

  const dispose = () => clearTimeout(realtimeRefreshTimer);

  return {
    isLoading,
    hasLoaded,
    loadError,
    resourceErrors,
    isInitialLoading,
    isRefreshing,
    hasResourceErrors,
    readResourceError,
    configureLoaders,
    fetchPageData,
    resetForAssistant,
    refreshActivityFromRealtime,
    dispose,
  };
}
